import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createRouter, RouterProvider } from '@tanstack/react-router';

import { signedOutSearch } from '@/features/auth/idleSession';
import { sessionQuery } from '@/features/auth/session';
import { watchBackTrail } from '@/layouts/backTarget';
import { setUnauthorizedHandler } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';
import { rootErrorHandlers, startSentry } from '@/lib/sentry';
import { startShake } from '@/lib/shake';
import { AppProviders } from '@/providers/AppProviders';
import { routeTree } from '@/routeTree.gen';
import { ROUTER_SCROLL } from '@/util/scroll';
import '@/styles/globals.css';

const router = createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    ...ROUTER_SCROLL,
});

declare module '@tanstack/react-router' {
    interface Register {
        router: typeof router;
    }
}

// Before render, unlike Shake: Sentry is there to see what goes wrong while the admin starts.
startSentry(router);

// Every subpage's back arrow points at the page the visitor came from, and this one subscription
// is what remembers it (ALS-105). Installed with the router rather than in a component, because
// the rule is application-wide and a screen must not be able to opt out of it.
watchBackTrail(router);

// A 401 on a request that expected a session means the session ended under us: the person
// goes back to the login screen as "expired", as signed out by the idle limit when that is what
// ran out (ALS-494), or as "changed" when the API says their account changed (ALS-525), and
// returns to this page once signed in again. Only the first request to hit the ended session is
// told why; the ones after it find no cookie, so a reason already on the login screen is kept
// rather than replaced by theirs.
setUnauthorizedHandler((problem) => {
    queryClient.removeQueries({ queryKey: sessionQuery.queryKey });
    const { href: here, search } = router.state.location;
    const onLogin = here.startsWith('/login');
    void router.navigate({
        to: '/login',
        search: {
            ...(onLogin && search.reason
                ? { reason: search.reason, minutes: search.minutes }
                : signedOutSearch(problem)),
            redirect: onLogin ? search.redirect : here,
        },
    });
});

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Root element #root not found');

createRoot(rootElement, rootErrorHandlers()).render(
    <StrictMode>
        <AppProviders>
            <RouterProvider router={router} />
        </AppProviders>
    </StrictMode>,
);

// After render on purpose: the admin mounts first, and a bug reporter is never on the
// critical path of the app it reports on. Module scope rather than an effect, so
// StrictMode's double invocation cannot reach it and no started-flag is needed.
void startShake();
